import { StackFrame } from "@sentry/solid";

import CreditLine from "@repo/kikoff-models/lending/CreditLine";
import Subscription from "@repo/kikoff-models/Subscription";
import { StructuredEvents } from "@repo/utils/Analytics";
import UObject from "@repo/utils/UObject";

import { OverlayKey } from "~/overlays";

import formatted = StructuredEvents.formatted;
import actions = StructuredEvents.actions;

import AsyncActions = StructuredEvents.AsyncActions;

const events = {
  sentry: {
    [formatted]: "Sentry",
    send: {
      [formatted]: "Send",
      [actions]: {} as {
        debug: {
          message: string | undefined;
          filestack: (string | undefined)[] | undefined;
          frames: UObject.Resolve<StackFrame>[] | undefined;
          suppress: boolean;
        };
      },
    },
  },
  page: {
    [formatted]: "Page",
    [actions]: {} as {
      impression:
        | {
            initial: true;
          }
        | {
            initial: false;
            from: string;
          };
    },
  },
  overlay: {
    [formatted]: "Overlay",
    [actions]: {} as {
      impression: {
        key: OverlayKey;
      };
    },
  },
  notFoundPage: {
    [formatted]: "404 Page",
    [actions]: {} as {
      impression: {};
    },
  },
  auth: {
    [formatted]: "Auth",
    [actions]: {} as {
      impression: {};
    },
    forgotPasswordButton: {
      [formatted]: "Forgot Password Button",
      [actions]: {} as {
        click: {};
      },
    },
    forgotEmailButton: {
      [formatted]: "Forgot Email Button",
      [actions]: {} as {
        click: {};
      },
    },
    resetPasswordButton: {
      [formatted]: "Reset Password Button",
      [actions]: {} as {
        click: {};
      },
    },
  },
  login: {
    [formatted]: "Login",
    [actions]: {} as AsyncActions<{}>,
    submitButton: {
      [formatted]: "Submit Button",
      [actions]: {} as {
        click: {};
      },
    },
  },
  googleSso: {
    [formatted]: "Google Sso",
    [actions]: {} as AsyncActions<{}> & {
      success: {
        authenticated: boolean;
      };
    },
  },
  onboarding: {
    [formatted]: "Onboarding",
    createAccount: {
      [formatted]: "Create Account",
      [actions]: {} as AsyncActions<{}> & {
        success: {
          authenticated: boolean;
        };
      },
      submitButton: {
        [formatted]: "Submit Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    emailVerificationScreen: {
      [formatted]: "Email Verification Screen",
      goToEmail: {
        [formatted]: "Go To Email Button",
        [actions]: {} as {
          click: {};
        },
      },
      checkEmailVerifiedButton: {
        [formatted]: "Check Email Verified Button",
        [actions]: {} as {
          click: {};
        },
      },
      resendEmailButton: {
        [formatted]: "Resend Email Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    emailVerifiedScreen: {
      [formatted]: "Email Verified Screen",
      continueButton: {
        [formatted]: "Continue Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    emailConfirmation: {
      [formatted]: "Email Confirmation",
      [actions]: {} as {
        impression: {
          status: "success" | "invalid" | "error";
        };
      },
    },
    piiEntry: {
      [formatted]: "PII Entry",
      continueButton: {
        [formatted]: "Continue Button",
        [actions]: {} as {
          click: {
            step:
              | "Name"
              | "Phone"
              | "Phone code"
              | "Birthdate"
              | "Address"
              | "National ID"
              | "Review";
          };
        },
      },
      continue: {
        [formatted]: "Continue",
        [actions]: {} as AsyncActions<{
          step:
            | "Name"
            | "Phone"
            | "Phone code"
            | "Birthdate"
            | "Address"
            | "National ID"
            | "Review";
        }>,
      },
      backButton: {
        [formatted]: "Back Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    idv: {
      [formatted]: "IDV",
      [actions]: {} as {
        impression: {};
      },
      nextButton: {
        [formatted]: "Next",
        [actions]: {} as {
          click: {};
        },
      },
      retryPii: {
        [formatted]: "Retry PII",
        [actions]: {} as {
          click: {};
        },
      },
      eCBSV: {
        [formatted]: "eCBSV",
        continueButton: {
          [formatted]: "Continue Button",
          [actions]: {} as {
            click: {};
          },
        },
      },
      manualUpload: {
        [formatted]: "Manual Upload",
        retryButton: {
          [formatted]: "Retry Button",
          [actions]: {} as {
            click: {};
          },
        },
        reuploadButton: {
          [formatted]: "Re-upload Button",
          [actions]: {} as {
            click: {};
          },
        },
      },
      creditFreeze: {
        [formatted]: "Credit Freeze",
        retryButton: {
          [formatted]: "Retry Button",
          [actions]: {} as {
            click: {};
          },
        },
      },
      frozenFileRedesign: {
        [formatted]: "Frozen File Redesign",
        phoneCollapsible: {
          [formatted]: "Phone Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        mailCollapsible: {
          [formatted]: "Mail Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        equifaxCollapsible: {
          [formatted]: "Equifax Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        experianCollapsible: {
          [formatted]: "Experian Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        retryButton: {
          [formatted]: "Retry",
          [actions]: {} as {
            click: {};
          },
        },
      },
      fraudAlert: {
        [formatted]: "Fraud Alert",
        phoneCollapsible: {
          [formatted]: "Phone Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        mailCollapsible: {
          [formatted]: "Mail Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        equifaxCollapsible: {
          [formatted]: "Equifax Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        experianCollapsible: {
          [formatted]: "Experian Collapsible",
          [actions]: {} as {
            click: {};
          },
        },
        retryButton: {
          [formatted]: "Retry",
          [actions]: {} as {
            click: {};
          },
        },
      },
    },
  },
  hdyhauSurvey: {
    [formatted]: "HDYHAU Survey",
    [actions]: {} as {
      impression: {};
    },
    submitButton: {
      [formatted]: "Submit Button",
      [actions]: {} as {
        click: {
          option: string;
          followup: string;
        };
      },
    },
  },
  creditAccount: {
    [formatted]: "Credit Account",
    open: {
      [formatted]: "Open",
      [actions]: {} as AsyncActions<{
        agreementId: CreditLine.Agreement.Id;
      }>,
    },
    payment: {
      [formatted]: "Payment",
      [actions]: {} as AsyncActions<{
        amountCents: number;
      }>,
    },
    close: {
      [formatted]: "Close",
      [actions]: {} as AsyncActions<{}>,
    },
  },
  paymentMethod: {
    [formatted]: "Payment Method",
    add: {
      [formatted]: "Add",
      [actions]: {} as AsyncActions<{}>,
    },
    remove: {
      [formatted]: "Remove",
      [actions]: {} as AsyncActions<{}>,
    },
  },
  subscription: {
    [formatted]: "Subscription",
    changePlan: {
      [formatted]: "Change Plan",
      [actions]: {} as AsyncActions<{
        from: Subscription.Tier;
        to: Subscription.Tier;
      }>,
    },
  },
  selectPlanUtilization: {
    [formatted]: "Select Plan Utilization",
    [actions]: {} as {
      impression: {
        variant: string;
        currentUtilizationPercentage?: number;
        utilizationPercentageByPlan?: Record<Subscription.Tier, number>;
        recommendedPlan?: Subscription.Tier;
      };
      changePlan: {
        plan: string;
        view: string;
      };
    },
  },
} as const satisfies StructuredEvents.Schema;

export default events;
